import * as createPropertygroups from 'store/actions/propertygroups/createPropertygroups';
import * as deletePropertygroups from 'store/actions/propertygroups/deletePropertygroups';
import * as fetchPropertygroups from 'store/actions/propertygroups/fetchPropertygroups';
import * as types from 'store/actions/types';
import * as updatePropertygroups from 'store/actions/propertygroups/updatePropertygroups';
import apiClient from 'clients/apiClient';
import config from 'helpers/config';
import { put, takeEvery } from 'redux-saga/effects';
import { showNotification } from 'store/actions/uiActions';
import utils from 'helpers/utils';

function* fetchPropertygroupsAsync({ payload }) {
    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/property-groups`;

        // Fetch the data.
        const response = yield apiClient.read(url, payload);

        // Send the data the success action.
        yield put(fetchPropertygroups.fetchPropertygroupsSuccess(response));
    } catch (error) {
        // Trigger the error action.
        yield put(fetchPropertygroups.fetchPropertygroupsFailure(error, payload));
    }
}

function* fetchPropertygroupsAccumulatedAsync({ payload }) {
    const params = payload.params || {
        size: payload.size,
        offset: payload.offset,
        sort_by: payload.sort.field,
        sort_direction: payload.sort.order,
    };

    yield fetchPropertygroupsAsync({
        payload: params,
    });
}

function* fetchPropertygroupAsync({ payload }) {
    const {
        id,
        onError,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/property-groups/${ id }`;

        // Fetch the data.
        const response = yield apiClient.read(url);

        // Send the data to the success action.
        yield put(fetchPropertygroups.fetchPropertygroupSuccess(response));

        // Trigger the localisations of that category.
        yield put(fetchPropertygroups.fetchPropertygroupLocalisation(payload));
    } catch (error) {
        // Trigger the error action.
        yield put(fetchPropertygroups.fetchPropertygroupFailure(error, payload));

        if (utils.isFunction(onError)) {
            onError(error);
        }
    }
}

function* fetchPropertygroupLocalisationAsync({ payload }) {
    const {
        id,
        onError,
        onSuccess,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/property-groups/${ id }/localisation`;

        // POST the data.
        const response = yield apiClient.read(url);

        // Send the data to the success action.
        yield put(fetchPropertygroups.fetchPropertygroupLocalisationSuccess(payload, response));

        if (utils.isFunction(onSuccess)) {
            onSuccess();
        }
    } catch (error) {
        // Trigger the error action.
        yield put(fetchPropertygroups.fetchPropertygroupLocalisationFailure(error, payload));

        if (utils.isFunction(onError)) {
            onError(error);
        }
    }
}

function* createPropertygroupAsync({ payload }) {
    const {
        onError,
        formValues,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/property-groups`;

        const response = yield apiClient.create(url, formValues.base);

        // Send new created property group and the payload to the localisation action.
        yield put(createPropertygroups.createPropertygroupLocalisation(payload, response));
    } catch (error) {
        // Trigger the error action.
        yield put(createPropertygroups.createPropertygroupFailure(error));

        yield onError(error);
    }
}

function* createPropertygroupLocalisationAsync({ payload, response }) {
    const {
        formValues,
        onError,
        onSuccess,
    } = payload;

    const {
        id,
    } = response;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/property-groups/${ id }/localisation`;

        // UPDATE the data.
        yield apiClient.update(url, formValues.localisation);

        // Send the data to the success action.
        yield put(createPropertygroups.createPropertygroupLocalisationSuccess(payload, response));

        yield onSuccess(response);
    } catch (error) {
        // Trigger the error action.
        yield put(createPropertygroups.createPropertygroupLocalisationFailure(error));

        yield onError(error);
    }
}

function* createPropertygroupLocalisationSuccessAsync() {
    yield put(showNotification('notifications.propertygroups.create.success'));
}

function* updatePropertygroupAsync({ payload }) {
    const {
        onError,
    } = payload;

    try {
        // Send the data through
        yield put(updatePropertygroups.updatePropertygroupLocalisation(payload));
    } catch (error) {
        // Trigger the error action.
        yield put(updatePropertygroups.updatePropertygroupFailure(error));

        yield onError(error);
    }
}

function* updatePropertygroupLocalisationAsync({ payload }) {
    const {
        formValues,
        id,
        onError,
        onSuccess,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/property-groups/${ id }/localisation`;

        // UPDATE the data.
        const response = yield apiClient.update(url, formValues.localisation);

        // Send the data to the success action.
        yield put(updatePropertygroups.updatePropertygroupLocalisationSuccess(payload, response));

        yield onSuccess();
    } catch (error) {
        // Trigger the error action.
        yield put(updatePropertygroups.updatePropertygroupLocalisationFailure(error));

        yield onError(error);
    }
}

function* updatePropertygroupLocalisationSuccessAsync() {
    yield put(showNotification('notifications.propertygroups.update.success'));
}

function* deletePropertyGroupAsync({ payload }) {
    const {
        id,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/property-groups/${ id }`;

        // Delete the data.
        yield apiClient.delete(url);

        yield put(deletePropertygroups.deletePropertyGroupSuccess(payload));
    } catch (error) {
        // Trigger the error action.
        yield put(deletePropertygroups.deletePropertyGroupFailure(error));
    }
}

function* deletePropertygroupSuccessAsync() {
    yield put(showNotification('notifications.propertygroups.delete.success', 'success'));
}

function* failureMessage({ error }) {
    const { message } = error.response;

    if (!message) {
        return;
    }

    yield put(showNotification(message, 'warning'));
}

export default function* propertygroupsSagas() {
    yield [
        takeEvery(types.FETCH_PROPERTYGROUPS, fetchPropertygroupsAsync),
        takeEvery(types.FETCH_PROPERTYGROUPS_FAILURE, failureMessage),
        takeEvery(types.FETCH_PROPERTYGROUPS_ACCUMULATED, fetchPropertygroupsAccumulatedAsync),

        takeEvery(types.FETCH_PROPERTYGROUP, fetchPropertygroupAsync),
        takeEvery(types.FETCH_PROPERTYGROUP_FAILURE, failureMessage),

        takeEvery(types.FETCH_PROPERTYGROUP_LOCALISATION, fetchPropertygroupLocalisationAsync),
        takeEvery(types.FETCH_PROPERTYGROUP_LOCALISATION_FAILURE, failureMessage),

        takeEvery(types.CREATE_PROPERTYGROUP, createPropertygroupAsync),
        takeEvery(types.CREATE_PROPERTYGROUP_FAILURE, failureMessage),

        takeEvery(types.CREATE_PROPERTYGROUP_LOCALISATION, createPropertygroupLocalisationAsync),
        takeEvery(types.CREATE_PROPERTYGROUP_LOCALISATION_FAILURE, failureMessage),
        takeEvery(types.CREATE_PROPERTYGROUP_LOCALISATION_SUCCESS, createPropertygroupLocalisationSuccessAsync),

        takeEvery(types.UPDATE_PROPERTYGROUP, updatePropertygroupAsync),
        takeEvery(types.UPDATE_PROPERTYGROUP_FAILURE, failureMessage),

        takeEvery(types.UPDATE_PROPERTYGROUP_LOCALISATION, updatePropertygroupLocalisationAsync),
        takeEvery(types.UPDATE_PROPERTYGROUP_LOCALISATION_FAILURE, failureMessage),
        takeEvery(types.UPDATE_PROPERTYGROUP_LOCALISATION_SUCCESS, updatePropertygroupLocalisationSuccessAsync),

        takeEvery(types.DELETE_PROPERTYGROUP, deletePropertyGroupAsync),
        takeEvery(types.DELETE_PROPERTYGROUP_FAILURE, failureMessage),
        takeEvery(types.DELETE_PROPERTYGROUP_SUCCESS, deletePropertygroupSuccessAsync),
    ];
}



// WEBPACK FOOTER //
// ./src/store/sagas/propertygroupsSagas.js